import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchListings,
  fetchSellerBookings,
  updateBookingStatus,
  fetchVerificationEvents,
  addVerificationEvent,
  fetchBookingHistory,
  createReview,
  registerSellerAlias,
  type Listing,
  type Booking,
  type VerificationEvent,
  type BookingStatusHistory,
  type ListingStatusHistory,
  type Review,
} from '../../features/marketplace/api';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../contexts/ToastContext';
import { getAllowedCredentials } from '../../lib/webauthn';

export function MarketplaceSellerPage() {
  const navigate = useNavigate();
  const [sellerAlias, setSellerAlias] = useState(localStorage.getItem('marketplace.sellerAlias') || '');
  const [listings, setListings] = useState<Listing[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [reviews, setReviews] = useState<Record<string, { buyerReview: Review | null, sellerReview: Review | null }>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bookingErrors, setBookingErrors] = useState<Record<string, string>>({});
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [listingStatusFilter, setListingStatusFilter] = useState<string>('all');
  const { showError, showSuccess } = useToast();

  // Registration state (used when no alias is set)
  const [registering, setRegistering] = useState(false);

  // Seller review modal state
  const [reviewingBooking, setReviewingBooking] = useState<Booking | null>(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');

  // Service completion verification modal state
  const [completingBooking, setCompletingBooking] = useState<Booking | null>(null);
  const [buyerCategory, setBuyerCategory] = useState<string>('Normal');
  const [sellerSignedAt, setSellerSignedAt] = useState<Date | null>(null);
  const [buyerSignedAt, setBuyerSignedAt] = useState<Date | null>(null);
  const [buyerQrData, setBuyerQrData] = useState<any>(null);
  const [verificationSubmitting, setVerificationSubmitting] = useState(false);
  const [signingInProgress, setSigningInProgress] = useState<'seller' | 'buyer' | null>(null);
  const [scanningQR, setScanningQR] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [qrScanError, setQrScanError] = useState<string | null>(null);

  useEffect(() => {
    if (sellerAlias) {
      localStorage.setItem('marketplace.sellerAlias', sellerAlias);
      loadSellerData();
    }
  }, [sellerAlias]);

  async function loadSellerData() {
    try {
      setLoading(true);
      const [listingsData, bookingsData] = await Promise.all([
        fetchListings(100),
        fetchSellerBookings(sellerAlias),
      ]);
      setListings((listingsData || []).filter((l) => l.sellerAlias === sellerAlias));
      setBookings(bookingsData || []);

      // Use embedded reviews from bookings (already included in the API response)
      const reviewsMap: Record<string, { buyerReview: Review | null, sellerReview: Review | null }> = {};
      for (const booking of bookingsData || []) {
        reviewsMap[booking.id] = {
          buyerReview: booking.buyerReview || null,
          sellerReview: booking.sellerReview || null,
        };
      }
      setReviews(reviewsMap);

      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load seller data');
      setListings([]);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdateBookingStatus(bookingId: string, newStatus: string) {
    try {
      // Clear any previous error for this booking
      setBookingErrors((prev) => {
        const updated = { ...prev };
        delete updated[bookingId];
        return updated;
      });

      await updateBookingStatus(bookingId, newStatus);
      await loadSellerData();

      // Show success message for important state transitions
      if (newStatus === 'confirmed') {
        showSuccess('Booking confirmed successfully');
      } else if (newStatus === 'completed') {
        showSuccess('Service marked as completed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update booking status';

      // Set error for this specific booking
      setBookingErrors((prev) => ({
        ...prev,
        [bookingId]: errorMessage
      }));

      // Also show toast for visibility
      showError(errorMessage);
    }
  }

  function handleScanBuyerQR() {
    // Open camera scanner modal
    setShowQrScanner(true);
    setQrScanError(null);
  }

  function handleQrScanSuccess(qrDataString: string) {
    try {
      // Parse QR data
      const qrData = JSON.parse(qrDataString);

      // Validate QR data
      if (!qrData.bookingId || !qrData.buyerAlias || !qrData.signedAt) {
        throw new Error('Invalid QR code data');
      }

      // Check if QR matches current booking
      if (completingBooking && qrData.bookingId !== completingBooking.id) {
        throw new Error('QR code is for a different booking');
      }

      // Check expiry
      const expiresAt = new Date(qrData.expiresAt);
      if (expiresAt < new Date()) {
        throw new Error('QR code has expired');
      }

      // Store buyer QR data and mark as signed
      setBuyerQrData(qrData);
      setBuyerSignedAt(new Date(qrData.signedAt));
      setShowQrScanner(false);
      showSuccess('Buyer signature verified from QR code!');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to parse QR code';
      setQrScanError(errorMessage);
      showError(errorMessage);
    }
  }

  async function handleManualQrInput() {
    const qrInput = prompt('Paste the QR code data from buyer:');
    if (qrInput) {
      handleQrScanSuccess(qrInput);
    }
  }

  async function handleSignWithWebAuthn(party: 'seller' | 'buyer') {
    try {
      setSigningInProgress(party);

      // Fetch allowed credentials from server to ensure only the logged-in user's credentials can be used
      const allowCredentials = await getAllowedCredentials();

      // Trigger WebAuthn authentication
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);

      const credential = await navigator.credentials.get({
        publicKey: {
          challenge,
          allowCredentials: allowCredentials, // ✅ Restrict to logged-in user's credentials
          timeout: 60000,
          userVerification: 'required',
        },
      }) as PublicKeyCredential | null;

      if (!credential) {
        throw new Error('WebAuthn signature cancelled');
      }

      // Successfully signed - record timestamp
      const signedAt = new Date();
      if (party === 'seller') {
        setSellerSignedAt(signedAt);
        showSuccess('Seller signature verified with WebAuthn');
      } else {
        setBuyerSignedAt(signedAt);
        showSuccess('Buyer signature verified with WebAuthn');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to sign with WebAuthn`;
      showError(errorMessage);
    } finally {
      setSigningInProgress(null);
    }
  }

  async function handleCompleteServiceWithVerification() {
    if (!completingBooking) return;

    if (!sellerSignedAt || !buyerSignedAt) {
      showError('Both seller and buyer must sign with WebAuthn');
      return;
    }

    try {
      setVerificationSubmitting(true);

      // Create verification event with WebAuthn signatures and buyer category
      const buyerInfo = buyerQrData
        ? `Buyer ${buyerQrData.buyerAlias} (QR verified: ${buyerQrData.signedAt})`
        : `Buyer WebAuthn Signed: ${buyerSignedAt.toISOString()}`;

      await addVerificationEvent(
        completingBooking.id,
        `service_completed - Seller: ${sellerAlias}, Buyer Category: ${buyerCategory}, Seller WebAuthn Signed: ${sellerSignedAt.toISOString()}, ${buyerInfo}`
      );

      // Update booking status to completed
      await updateBookingStatus(completingBooking.id, 'completed');

      // Close modal and reset form
      setCompletingBooking(null);
      setBuyerCategory('Normal');
      setSellerSignedAt(null);
      setBuyerSignedAt(null);
      setBuyerQrData(null);

      // Reload data
      await loadSellerData();

      showSuccess(`Service completed! Buyer classified as: ${buyerCategory}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to complete service';
      showError(errorMessage);
    } finally {
      setVerificationSubmitting(false);
    }
  }

  async function handleSubmitSellerReview() {
    if (!reviewingBooking) return;

    try {
      const currentBookingId = reviewingBooking.id;

      // Create seller review
      await createReview(
        currentBookingId,
        reviewRating,
        reviewComment,
        'seller_to_buyer',
        sellerAlias
      );

      // Don't update booking status yet - wait for buyer to review too
      // Status will be updated to 'reviewed' when both reviews are submitted

      // Close modal and reset form
      setReviewingBooking(null);
      setReviewRating(5);
      setReviewComment('');

      // Reload data to refresh the bookings list
      await loadSellerData();

      showSuccess('Review submitted successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit review';
      showError(errorMessage);
    }
  }

  function handleEditListing(listing: Listing) {
    navigate(`/marketplace/edit/${listing.id}`, { state: { listing } });
  }

  if (!sellerAlias) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">Register Your Seller Alias</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Choose a unique alias to use as a seller. This will be locked to your account and identify you in the marketplace.
            </p>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const alias = formData.get('alias') as string;

                if (!alias.trim()) return;

                try {
                  setRegistering(true);

                  // Register alias with backend
                  await registerSellerAlias(alias.trim());

                  // Save to state and localStorage
                  setSellerAlias(alias.trim());
                  showSuccess(`Successfully registered seller alias: ${alias.trim()}`);
                } catch (err) {
                  showError(err instanceof Error ? err.message : 'Failed to register seller alias');
                } finally {
                  setRegistering(false);
                }
              }}
            >
              <input
                name="alias"
                type="text"
                placeholder="Your seller alias"
                className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-4 py-3 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent mb-4"
                required
                disabled={registering}
              />
              <Button type="submit" className="w-full" disabled={registering}>
                {registering ? 'Registering...' : 'Register & Continue'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Seller Dashboard</h1>
              <p className="text-sm text-slate-600 dark:text-slate-400">Managing as: {sellerAlias}</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => navigate('/marketplace')}>
                Browse Listings
              </Button>
              <Button onClick={() => navigate('/marketplace/create')}>
                + Create Listing
              </Button>
              <Button
                onClick={() => {
                  localStorage.removeItem('marketplace.sellerAlias');
                  setSellerAlias('');
                }}
                variant="secondary"
              >
                Change Alias
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400 mb-6">{error}</div>
        )}

        {loading ? (
          <div className="text-center text-slate-600 dark:text-slate-400">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Listings */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-200">Your Listings</h3>
              </div>

              {/* Listing Status Filter */}
              <div className="flex gap-2 mb-4 flex-wrap">
                <button
                  onClick={() => setListingStatusFilter('all')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    listingStatusFilter === 'all'
                      ? 'bg-brand-600 text-white'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
                  }`}
                >
                  All ({listings.length})
                </button>
                <button
                  onClick={() => setListingStatusFilter('pending_approval')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    listingStatusFilter === 'pending_approval'
                      ? 'bg-yellow-600 text-white'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
                  }`}
                >
                  Pending ({listings.filter(l => l.status === 'pending_approval').length})
                </button>
                <button
                  onClick={() => setListingStatusFilter('approved')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    listingStatusFilter === 'approved'
                      ? 'bg-green-600 text-white'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
                  }`}
                >
                  Approved ({listings.filter(l => l.status === 'approved').length})
                </button>
                <button
                  onClick={() => setListingStatusFilter('rejected')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    listingStatusFilter === 'rejected'
                      ? 'bg-red-600 text-white'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
                  }`}
                >
                  Rejected ({listings.filter(l => l.status === 'rejected').length})
                </button>
              </div>

              {listings.length === 0 ? (
                <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6 text-center text-slate-600 dark:text-slate-500">
                  No listings yet. Create one to get started!
                </div>
              ) : listings.filter(listing => listingStatusFilter === 'all' || listing.status === listingStatusFilter).length === 0 ? (
                <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6 text-center text-slate-600 dark:text-slate-500">
                  No listings with status: {listingStatusFilter === 'pending_approval' ? 'Pending' : listingStatusFilter}
                </div>
              ) : (
                <div className="space-y-3">
                  {listings
                    .filter(listing => listingStatusFilter === 'all' || listing.status === listingStatusFilter)
                    .map((listing) => (
                    <div key={listing.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                      <div className="flex">
                        {/* Image Thumbnail */}
                        {listing.media && listing.media.length > 0 ? (
                          <div className="w-24 h-24 bg-slate-100 dark:bg-slate-900 flex-shrink-0 flex items-center justify-center overflow-hidden">
                            <img
                              src={listing.media[0]}
                              alt={listing.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.parentElement!.innerHTML = '<div class="text-slate-600 text-xs p-2">No image</div>';
                              }}
                            />
                          </div>
                        ) : (
                          <div className="w-24 h-24 bg-slate-100 dark:bg-slate-900 flex-shrink-0 flex items-center justify-center">
                            <svg className="w-8 h-8 text-slate-400 dark:text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}

                        {/* Content */}
                        <div className="flex-1 p-4">
                          <div className="flex items-start justify-between">
                            <h4 className="font-semibold text-slate-900 dark:text-slate-100">{listing.title}</h4>
                            {/* Status Badge */}
                            <span
                              className={`px-2 py-1 rounded text-xs font-semibold ml-2 ${
                                listing.status === 'approved'
                                  ? 'bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-800 text-green-700 dark:text-green-400'
                                  : listing.status === 'rejected'
                                    ? 'bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400'
                                    : 'bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-800 text-yellow-700 dark:text-yellow-400'
                              }`}
                            >
                              {listing.status === 'pending_approval' ? 'Pending' : listing.status}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 line-clamp-1">{listing.description}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-brand-600 dark:text-brand-400 font-semibold">
                              {listing.currency} {(listing.priceCents / 100).toFixed(2)}
                            </span>
                            <span className="text-xs text-slate-600 dark:text-slate-500">
                              {new Date(listing.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          {/* Edit Button */}
                          <button
                            onClick={() => handleEditListing(listing)}
                            className="mt-2 text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                      {/* Status History */}
                      <StatusHistorySection history={listing.history || []} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Bookings */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-200">Incoming Bookings</h3>
              </div>

              {/* Status Filter */}
              <div className="flex gap-2 mb-4 flex-wrap">
                <button
                  onClick={() => setStatusFilter('all')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    statusFilter === 'all'
                      ? 'bg-brand-600 text-white'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
                  }`}
                >
                  All ({bookings.length})
                </button>
                <button
                  onClick={() => setStatusFilter('requested')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    statusFilter === 'requested'
                      ? 'bg-yellow-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  Requested ({bookings.filter(b => b.status === 'requested').length})
                </button>
                <button
                  onClick={() => setStatusFilter('confirmed')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    statusFilter === 'confirmed'
                      ? 'bg-green-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  Confirmed ({bookings.filter(b => b.status === 'confirmed').length})
                </button>
                <button
                  onClick={() => setStatusFilter('verified')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    statusFilter === 'verified'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  Verified ({bookings.filter(b => b.status === 'verified').length})
                </button>
                <button
                  onClick={() => setStatusFilter('in_progress')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    statusFilter === 'in_progress'
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  In Progress ({bookings.filter(b => b.status === 'in_progress').length})
                </button>
                <button
                  onClick={() => setStatusFilter('completed')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    statusFilter === 'completed'
                      ? 'bg-teal-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  Completed ({bookings.filter(b => b.status === 'completed').length})
                </button>
                <button
                  onClick={() => setStatusFilter('reviewed')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    statusFilter === 'reviewed'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  Reviewed ({bookings.filter(b => b.status === 'reviewed').length})
                </button>
              </div>

              {bookings.length === 0 ? (
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 text-center text-slate-500">
                  No bookings yet
                </div>
              ) : bookings.filter(booking => statusFilter === 'all' || booking.status === statusFilter).length === 0 ? (
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 text-center text-slate-500">
                  No bookings with status: {statusFilter}
                </div>
              ) : (
                <div className="space-y-3">
                  {bookings
                    .filter(booking => statusFilter === 'all' || booking.status === statusFilter)
                    .map((booking) => {
                    const listing = listings.find((l) => l.id === booking.listingId);
                    return (
                      <div key={booking.id} className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="font-semibold text-slate-100">{listing?.title || 'Unknown Listing'}</div>
                            <div className="text-sm text-slate-400">
                              Buyer:{' '}
                              <button
                                onClick={() => navigate(`/marketplace/buyer-profile/${booking.buyerAlias}`)}
                                className="text-blue-400 hover:text-blue-300 hover:underline"
                              >
                                {booking.buyerAlias}
                              </button>
                            </div>
                          </div>
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold ${
                              booking.status === 'requested'
                                ? 'bg-yellow-900/30 border border-yellow-800 text-yellow-400'
                                : booking.status === 'confirmed'
                                  ? 'bg-green-900/30 border border-green-800 text-green-400'
                                  : booking.status === 'verified'
                                    ? 'bg-blue-900/30 border border-blue-800 text-blue-400'
                                    : booking.status === 'in_progress'
                                      ? 'bg-purple-900/30 border border-purple-800 text-purple-400'
                                      : booking.status === 'completed'
                                        ? 'bg-teal-900/30 border border-teal-800 text-teal-400'
                                        : booking.status === 'reviewed'
                                          ? 'bg-indigo-900/30 border border-indigo-800 text-indigo-400'
                                          : booking.status === 'rejected'
                                            ? 'bg-red-900/30 border border-red-800 text-red-400'
                                            : booking.status === 'no_show'
                                              ? 'bg-orange-900/30 border border-orange-800 text-orange-400'
                                              : booking.status === 'cancelled'
                                                ? 'bg-gray-900/30 border border-gray-800 text-gray-400'
                                                : 'bg-slate-900/30 border border-slate-800 text-slate-400'
                            }`}
                          >
                            {booking.status === 'in_progress' ? 'In Progress' :
                             booking.status === 'no_show' ? 'No Show' :
                             booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                          </span>
                        </div>
                        <div className="text-xs text-slate-400 mb-3 space-y-1">
                          <div>
                            <span className="text-slate-500">Scheduled for:</span>{' '}
                            <span className="font-semibold text-slate-200">
                              {new Date(booking.scheduledFor).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                              {' at '}
                              {new Date(booking.scheduledFor).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500">
                            Requested: {new Date(booking.createdAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}
                            {' at '}
                            {new Date(booking.createdAt).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>

        {/* State 1: Requested - Seller can accept or reject */}
        {booking.status === 'requested' && (
          <div className="flex gap-2">
            <Button
                              onClick={() => handleUpdateBookingStatus(booking.id, 'confirmed')}
                              className="text-xs py-1 px-3 bg-green-700 hover:bg-green-600"
                            >
                              ✓ Accept
                            </Button>
                            <Button
                              onClick={() => handleUpdateBookingStatus(booking.id, 'rejected')}
                              className="text-xs py-1 px-3 bg-red-700 hover:bg-red-600"
                            >
                              ✕ Reject
                            </Button>
                          </div>
                        )}

                        {/* State 3: Confirmed - Wait for customer or mark no-show */}
                        {booking.status === 'confirmed' && (
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleUpdateBookingStatus(booking.id, 'verified')}
                              className="text-xs py-1 px-3 bg-blue-700 hover:bg-blue-600"
                            >
                              Customer Arrived
                            </Button>
                            <Button
                              onClick={() => handleUpdateBookingStatus(booking.id, 'no_show')}
                              className="text-xs py-1 px-3 bg-orange-700 hover:bg-orange-600"
                            >
                              No Show
                            </Button>
                          </div>
                        )}

                        {/* State 5: Verified - Start service or quick end */}
                        {booking.status === 'verified' && (
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleUpdateBookingStatus(booking.id, 'in_progress')}
                              className="text-xs py-1 px-3 bg-green-700 hover:bg-green-600"
                            >
                              Start Service
                            </Button>
                            <Button
                              onClick={() => handleUpdateBookingStatus(booking.id, 'cancelled')}
                              className="text-xs py-1 px-3 bg-gray-700 hover:bg-gray-600"
                            >
                              Quick End
                            </Button>
                          </div>
                        )}

                        {/* State 6: In Progress - End service with verification */}
                        {booking.status === 'in_progress' && (
                          <div className="flex gap-2">
                            <Button
                              onClick={() => {
                                setCompletingBooking(booking);
                                setBuyerCategory('Normal');
                                setSellerSignedAt(null);
                                setBuyerSignedAt(null);
                                setBuyerQrData(null);
                              }}
                              className="text-xs py-1 px-3 bg-green-700 hover:bg-green-600"
                            >
                              End Service
                            </Button>
                            <Button
                              onClick={() => handleUpdateBookingStatus(booking.id, 'cancelled')}
                              className="text-xs py-1 px-3 bg-gray-700 hover:bg-gray-600"
                            >
                              Quick End
                            </Button>
                          </div>
                        )}

                        {/* State 7: Completed - Review Required */}
                        {(booking.status === 'completed' || booking.status === 'reviewed') && !reviews[booking.id]?.sellerReview && (
                          <div className="bg-blue-900/20 border-2 border-blue-600 rounded p-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-blue-400 text-sm">⚠️</span>
                                <span className="text-xs font-semibold text-blue-300">Review Required</span>
                              </div>
                              <Button
                                onClick={() => {
                                  setReviewingBooking(booking);
                                  setReviewRating(5);
                                  setReviewComment('');
                                }}
                                className="text-xs py-1 px-3 bg-blue-700 hover:bg-blue-600"
                              >
                                Leave Review
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Final states - No actions */}
                        {(booking.status === 'rejected' || booking.status === 'no_show' || booking.status === 'cancelled' ||
                          (booking.status === 'reviewed' && reviews[booking.id]?.sellerReview)) && (
                          <div className="text-xs text-slate-500 italic">
                            {booking.status === 'rejected' && 'Booking was rejected'}
                            {booking.status === 'no_show' && 'Customer did not show up'}
                            {booking.status === 'cancelled' && 'Booking was cancelled'}
                            {booking.status === 'reviewed' && reviews[booking.id]?.sellerReview && 'Booking completed and reviewed'}
                          </div>
                        )}

                        {/* Reviews Display */}
                        {reviews[booking.id] && (reviews[booking.id].buyerReview || reviews[booking.id].sellerReview) && (
                          <div className="mt-3 pt-3 border-t border-slate-700 space-y-2">
                            {/* Customer Review (buyer-to-seller) */}
                            {reviews[booking.id].buyerReview && (
                              <div className="bg-slate-900 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-xs font-semibold text-slate-300">Customer Review</span>
                                  <span className="text-yellow-400">
                                    {'★'.repeat(reviews[booking.id].buyerReview!.rating)}{'☆'.repeat(5 - reviews[booking.id].buyerReview!.rating)}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-300">{reviews[booking.id].buyerReview!.comment}</p>
                                <div className="text-xs text-slate-500 mt-2">
                                  {new Date(reviews[booking.id].buyerReview!.createdAt).toLocaleDateString()}
                                </div>
                              </div>
                            )}

                            {/* Your Review (seller-to-buyer) */}
                            {reviews[booking.id].sellerReview && (
                              <div className="bg-slate-900 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-xs font-semibold text-blue-400">Your Review</span>
                                  <span className="text-yellow-400">
                                    {'★'.repeat(reviews[booking.id].sellerReview!.rating)}{'☆'.repeat(5 - reviews[booking.id].sellerReview!.rating)}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-300">{reviews[booking.id].sellerReview!.comment}</p>
                                <div className="text-xs text-slate-500 mt-2">
                                  {new Date(reviews[booking.id].sellerReview!.createdAt).toLocaleDateString()}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        <VerificationLog bookingId={booking.id} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Service Completion Verification Modal */}
      {completingBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg border-2 border-green-600 max-w-md w-full p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-green-400 text-2xl">✓</span>
              <h3 className="text-xl font-bold text-slate-100">Complete Service</h3>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              Verify physical presence and classify buyer to complete the service.
            </p>

            <div className="space-y-4">
              {/* Booking Info */}
              <div className="bg-slate-900 rounded p-3">
                <div className="text-sm text-slate-400">Completing service for:</div>
                <div className="text-sm font-medium text-slate-200">{completingBooking.buyerAlias}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {new Date(completingBooking.scheduledFor).toLocaleString()}
                </div>
              </div>

              {/* Buyer Category */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Buyer Category <span className="text-red-400">*</span>
                </label>
                <select
                  value={buyerCategory}
                  onChange={(e) => setBuyerCategory(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-slate-100 text-sm"
                >
                  <option value="Normal">Normal</option>
                  <option value="Observable">Observable</option>
                  <option value="Advance">Advance</option>
                  <option value="Superior">Superior</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Classify buyer based on service quality expectations
                </p>
              </div>

              {/* Seller Signature */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Seller Signature <span className="text-red-400">*</span>
                </label>
                {sellerSignedAt ? (
                  <div className="flex items-center gap-2 px-3 py-2 bg-green-900/20 border border-green-700 rounded">
                    <span className="text-green-400">✓</span>
                    <span className="text-sm text-green-300">
                      Signed at {sellerSignedAt.toLocaleTimeString()}
                    </span>
                  </div>
                ) : (
                  <Button
                    onClick={() => handleSignWithWebAuthn('seller')}
                    disabled={signingInProgress !== null}
                    className="w-full bg-blue-700 hover:bg-blue-600 disabled:bg-gray-600"
                  >
                    {signingInProgress === 'seller' ? 'Signing...' : '🔐 Sign with WebAuthn'}
                  </Button>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  Use your security key or biometric to confirm physical presence
                </p>
              </div>

              {/* Buyer Signature */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Buyer Signature <span className="text-red-400">*</span>
                </label>
                {buyerSignedAt ? (
                  <div className="bg-green-900/20 border border-green-700 rounded p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-green-400">✓</span>
                      <span className="text-sm text-green-300 font-medium">
                        Verified from QR Code
                      </span>
                    </div>
                    {buyerQrData && (
                      <div className="text-xs text-slate-400 space-y-1">
                        <div>Buyer: {buyerQrData.buyerAlias}</div>
                        <div>Signed: {new Date(buyerQrData.signedAt).toLocaleString()}</div>
                        <div>Method: WebAuthn on buyer's device</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Button
                    onClick={handleScanBuyerQR}
                    disabled={scanningQR}
                    className="w-full bg-purple-700 hover:bg-purple-600 disabled:bg-gray-600"
                  >
                    {scanningQR ? 'Scanning...' : '📱 Scan Buyer QR Code'}
                  </Button>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  Ask buyer to show their verification QR code from their device
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-2">
                <Button
                  onClick={handleCompleteServiceWithVerification}
                  disabled={verificationSubmitting || !sellerSignedAt || !buyerSignedAt}
                  className="w-full bg-green-700 hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed font-semibold"
                >
                  {verificationSubmitting ? 'Processing...' : 'Complete Service'}
                </Button>
                {(!sellerSignedAt || !buyerSignedAt) && (
                  <p className="text-xs text-slate-400 text-center">
                    Both parties must sign with WebAuthn to complete service
                  </p>
                )}
                <Button
                  onClick={() => {
                    setCompletingBooking(null);
                    setBuyerCategory('Normal');
                    setSellerSignedAt(null);
                    setBuyerSignedAt(null);
                    setBuyerQrData(null);
                  }}
                  disabled={verificationSubmitting}
                  className="w-full bg-slate-700 hover:bg-slate-600"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Scanner Modal */}
      {showQrScanner && (
        <QrScannerModal
          onScanSuccess={handleQrScanSuccess}
          onClose={() => setShowQrScanner(false)}
          onManualInput={handleManualQrInput}
          error={qrScanError}
        />
      )}

      {/* Seller Review Modal - Required */}
      {reviewingBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg border-2 border-blue-600 max-w-md w-full p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-blue-400 text-2xl">⚠️</span>
              <h3 className="text-xl font-bold text-slate-100">Review Required</h3>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              Please review this customer to complete the booking and help maintain marketplace quality.
            </p>

            <div className="space-y-4">
              {/* Booking Info */}
              <div className="bg-slate-900 rounded p-3">
                <div className="text-sm text-slate-400">Reviewing booking for:</div>
                <div className="text-sm font-medium text-slate-200">{reviewingBooking.buyerAlias}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {new Date(reviewingBooking.scheduledFor).toLocaleString()}
                </div>
              </div>

              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Rating
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setReviewRating(star)}
                      className="text-3xl transition-colors"
                    >
                      {star <= reviewRating ? '★' : '☆'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Comment
                </label>
                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-slate-100 text-sm resize-none"
                  rows={4}
                  placeholder="Share your experience with this customer..."
                />
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-2">
                <Button
                  onClick={handleSubmitSellerReview}
                  disabled={!reviewComment.trim()}
                  className="w-full bg-blue-700 hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed font-semibold"
                >
                  Submit Review
                </Button>
                {!reviewComment.trim() && (
                  <p className="text-xs text-slate-400 text-center">
                    Please add a comment to submit your review
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusHistorySection({ history }: { history: ListingStatusHistory[] }) {
  const [showHistory, setShowHistory] = useState(false);

  if (!history || history.length === 0) {
    return null;
  }

  return (
    <div className="px-4 pb-3 border-t border-slate-700 mt-2">
      <button
        onClick={() => setShowHistory(!showHistory)}
        className="flex items-center justify-between w-full pt-2 text-xs font-semibold text-slate-400 hover:text-slate-300"
      >
        <span>Status Updates ({history.length})</span>
        <span>{showHistory ? '▲' : '▼'}</span>
      </button>

      {showHistory && (
        <div className="mt-2 space-y-2">
          {history.map((item) => (
            <div key={item.id} className="bg-slate-900 rounded px-3 py-2">
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`text-xs font-semibold ${
                    item.status === 'approved'
                      ? 'text-green-400'
                      : item.status === 'rejected'
                        ? 'text-red-400'
                        : 'text-yellow-400'
                  }`}
                >
                  {item.status.charAt(0).toUpperCase() + item.status.slice(1).replace('_', ' ')}
                </span>
                <span className="text-xs text-slate-500">
                  {new Date(item.createdAt).toLocaleDateString()}
                </span>
              </div>
              {item.adminComment && (
                <p className="text-xs text-slate-300 italic">
                  "{item.adminComment}"
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QrScannerModal({
  onScanSuccess,
  onClose,
  onManualInput,
  error,
}: {
  onScanSuccess: (data: string) => void;
  onClose: () => void;
  onManualInput: () => void;
  error: string | null;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const scanIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  async function startCamera() {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }, // Prefer back camera on mobile
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play();
      }
      setScanning(true);
      startScanning();
    } catch (err) {
      setCameraError('Failed to access camera. Please grant camera permissions.');
      console.error('Camera error:', err);
    }
  }

  function stopCamera() {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
    }
  }

  function startScanning() {
    // Simple QR code detection using canvas
    scanIntervalRef.current = window.setInterval(() => {
      if (videoRef.current && canvasRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        const ctx = canvas.getContext('2d');

        if (ctx) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          // Try to detect QR using native BarcodeDetector if available
          if ('BarcodeDetector' in window) {
            const barcodeDetector = new (window as any).BarcodeDetector({ formats: ['qr_code'] });
            barcodeDetector.detect(canvas).then((barcodes: any[]) => {
              if (barcodes.length > 0) {
                onScanSuccess(barcodes[0].rawValue);
                stopCamera();
              }
            }).catch(() => {
              // Silently fail if detection doesn't work
            });
          }
        }
      }
    }, 300); // Scan every 300ms
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4">
      <div className="bg-slate-800 rounded-lg border-2 border-purple-600 max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-purple-400 text-2xl">📷</span>
            <h3 className="text-xl font-bold text-slate-100">Scan QR Code</h3>
          </div>
          <button
            onClick={() => {
              stopCamera();
              onClose();
            }}
            className="text-slate-400 hover:text-slate-200"
          >
            ✕
          </button>
        </div>

        <p className="text-sm text-slate-400 mb-4">
          Point your camera at the buyer's verification QR code
        </p>

        {/* Camera View */}
        <div className="relative bg-black rounded-lg overflow-hidden mb-4" style={{ aspectRatio: '4/3' }}>
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted
          />
          <canvas ref={canvasRef} className="hidden" />

          {/* Scanning Overlay */}
          {scanning && !error && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-64 h-64 border-4 border-purple-500 rounded-lg animate-pulse" />
            </div>
          )}

          {/* Camera Error */}
          {cameraError && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-75">
              <div className="text-center px-4">
                <span className="text-4xl mb-2 block">⚠️</span>
                <p className="text-sm text-red-400">{cameraError}</p>
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/20 border border-red-800 rounded p-3 mb-4">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Manual Input Option */}
        <div className="space-y-2">
          <button
            onClick={() => {
              stopCamera();
              onManualInput();
            }}
            className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded text-sm"
          >
            Enter QR Data Manually
          </button>

          <p className="text-xs text-center text-slate-500">
            {!('BarcodeDetector' in window) && 'Camera QR scanning not supported on this browser. Please use manual input.'}
          </p>
        </div>
      </div>
    </div>
  );
}

function VerificationLog({ bookingId }: { bookingId: string }) {
  const [history, setHistory] = useState<BookingStatusHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (expanded && history.length === 0) {
      loadHistory();
    }
  }, [expanded, bookingId]);

  async function loadHistory() {
    try {
      setLoading(true);
      const data = await fetchBookingHistory(bookingId);
      setHistory(data || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load booking history');
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mt-3 pt-3 border-t border-slate-700">
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-xs font-semibold text-slate-400 uppercase hover:text-slate-300"
        >
          <span>{expanded ? '▼' : '▶'}</span>
          <span>Booking History</span>
        </button>
      </div>

      {!expanded && null}

      {expanded && (
        <>
          {loading ? (
            <div className="text-xs text-slate-500">Loading...</div>
          ) : error ? (
            <div className="text-xs text-red-400">{error}</div>
          ) : !history || history.length === 0 ? (
            <div className="text-xs text-slate-500">No history available</div>
          ) : (
            <div className="space-y-2">
              {history.map((entry) => (
                <div key={entry.id} className="bg-slate-900 rounded px-3 py-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="text-xs font-medium text-slate-200">{entry.action}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        by {entry.actor}
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 whitespace-nowrap">
                      {new Date(entry.createdAt).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
